"use client"

import { useState } from "react"

type Props = {
  variantId: string
  onChange: (variantId: string, quantity: number) => void
  label?: string
  initial?: number
  /** carton size (variant metadata carton_qty): shows a one-tap "+ carton" button */
  carton?: number
  /** extra % off by the carton for this customer (carton-offer job); 0 = no offer shown */
  cartonSave?: number
}

/** Accessible quantity stepper (shift+click or shift+arrow = ±10). */
const BulkTableQuantity = ({ variantId, onChange, label = "Quantity", initial = 0, carton, cartonSave = 0 }: Props) => {
  const [quantity, setQuantity] = useState(String(initial))
  const commit = (q: number) => {
    const v = Math.max(0, Math.min(9999, Math.floor(isFinite(q) ? q : 0)))
    setQuantity(String(v))
    onChange(variantId, v)
  }
  const step = (e: { shiftKey: boolean }, dir: 1 | -1) => commit(Number(quantity) + dir * (e.shiftKey ? 10 : 1))

  const stepper = (
    <div className="flex items-center justify-end gap-1">
      <button type="button" aria-label={`Decrease ${label.toLowerCase()}`} onClick={(e) => step(e, -1)} className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-elevate-grey-light text-lg">
        −
      </button>
      <input
        aria-label={label}
        inputMode="numeric"
        value={quantity}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "")
          setQuantity(digits)
          onChange(variantId, Number(digits || 0))
        }}
        onBlur={() => commit(Number(quantity || 0))}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") { e.preventDefault(); step(e, 1) }
          if (e.key === "ArrowDown") { e.preventDefault(); step(e, -1) }
        }}
        className="h-10 w-12 rounded-lg border border-elevate-border text-center text-sm focus:border-elevate-black focus:outline-none"
      />
      <button type="button" aria-label={`Increase ${label.toLowerCase()}`} onClick={(e) => step(e, 1)} className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-elevate-grey-light text-lg">
        +
      </button>
    </div>
  )
  if (!carton || carton < 2) return stepper
  return (
    <div className="flex flex-col items-end gap-1">
      {stepper}
      <button type="button" onClick={() => commit(Number(quantity || 0) + carton)} className="text-xs underline underline-offset-2 text-elevate-grey-mid hover:text-elevate-black" aria-label={`Add a carton of ${carton} to ${label.toLowerCase()}`}>
        + carton ({carton}){cartonSave > 0 ? ` · ${cartonSave}% off` : ""}
      </button>
    </div>
  )
}

export default BulkTableQuantity
